/**
 * @vitacare/api-client
 *
 * Thin wrapper around the generated Hey API client.
 *
 * Usage:
 *   import { configureApiClient } from "@vitacare/api-client";
 *   configureApiClient({
 *     baseUrl: import.meta.env.VITE_API_BASE_URL,
 *     withCredentials: true,  // send/receive session + CSRF cookies
 *   });
 *
 *   import { listCommunityPosts } from "@vitacare/api-client";
 *   const { data, error } = await listCommunityPosts({ query: { page: 0 } });
 *
 * The `generated/` directory is produced by `task gen:api` and must not
 * be hand-edited. See packages/api-spec/README.md for the workflow.
 */

import { client } from "./generated/client.gen";

export interface ApiClientConfig {
  baseUrl: string;
  /**
   * When true, every request is sent with `credentials: "include"` so the
   * browser carries the session cookie (VITACARE_SESSION) across
   * subdomains, and a CSRF interceptor automatically reads the
   * `XSRF-TOKEN` cookie and mirrors it in the `X-XSRF-TOKEN` header on
   * mutating methods.
   */
  withCredentials?: boolean;
  /** Optional bearer token supplier (re-evaluated on every request). */
  getAuthToken?: () => string | null | undefined;
  /** Extra fetch options forwarded to every request. */
  fetch?: typeof fetch;
}

const MUTATING_METHODS = new Set(["POST", "PUT", "PATCH", "DELETE"]);

function readCookie(name: string): string | undefined {
  if (typeof document === "undefined") return undefined;
  const prefix = `${name}=`;
  for (const part of document.cookie.split(";")) {
    const s = part.trim();
    if (s.startsWith(prefix)) return decodeURIComponent(s.slice(prefix.length));
  }
  return undefined;
}

export function configureApiClient(config: ApiClientConfig): void {
  client.setConfig({
    baseUrl: config.baseUrl,
    fetch: config.fetch,
    ...(config.withCredentials ? { credentials: "include" as const } : {}),
  });

  if (config.withCredentials) {
    client.interceptors.request.use((request) => {
      if (MUTATING_METHODS.has(request.method.toUpperCase())) {
        const token = readCookie("XSRF-TOKEN");
        if (token) request.headers.set("X-XSRF-TOKEN", token);
      }
      return request;
    });
  }

  if (config.getAuthToken) {
    client.interceptors.request.use((request) => {
      const token = config.getAuthToken?.();
      if (token) {
        request.headers.set("Authorization", `Bearer ${token}`);
      }
      return request;
    });
  }
}

export { client };
export { formDataBodySerializer } from "@hey-api/client-fetch";
export * from "./generated";
